import { useEffect } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';

import { messagesState, sessionState } from '@chainlit/react-client';

import { preEditSnapshotState } from '@/state/project';

/**
 * Restores the chat when an edit-triggered rewind is cancelled.
 *
 * Editing an older message optimistically slices `messagesState` (UserMessage)
 * before the backend runs its `on_before_message_edit` rewind modal. If the
 * operator cancels that modal the backend aborts the edit and emits
 * `edit_message_cancelled`; this listener restores the pre-edit messages so the
 * cancel is a true no-op, matching the Rewind button.
 */
export default function EditRewindListener() {
  const session = useRecoilValue(sessionState);

  const restore = useRecoilCallback(
    ({ snapshot, set }) =>
      async (payload: { id?: string }) => {
        const pending = await snapshot.getPromise(preEditSnapshotState);
        if (!pending || (payload?.id && pending.message_id !== payload.id)) {
          return;
        }
        set(messagesState, pending.messages);
        set(preEditSnapshotState, null);
      },
    []
  );

  useEffect(() => {
    const socket = session?.socket;
    if (!socket) return;
    socket.on('edit_message_cancelled', restore);
    return () => {
      socket.off('edit_message_cancelled', restore);
    };
  }, [session?.socket, restore]);

  return null;
}
